import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import Handler from "../../../Helpers/Handler";
import { textCapitalize } from "../../../Helpers/Utils";
import { Skeleton } from "../../../Components/Needed";
import { useTrackedForm, useUnsaved } from "../unsaved";

// How many hours from now a booking's startDate must be, one value per
// portal - what BookingUtils.checkLeadTime() actually reads and enforces on
// every quote()/prebook call, Admin/Customer/Staff alike. Staff has no
// booking-creation flow yet, so its number is stored and ready but has
// nothing to apply to until that gets built.
const emptyForm = { admin: "3", customer: "3", staff: "3" };

const FIELDS = [
    { key: "admin", label: "Admin (hours)", hint: "Registration page" },
    { key: "customer", label: "Customer (hours)", hint: "Checkout page" },
    { key: "staff", label: "Staff (hours)", hint: "Not live yet - staff booking registration doesn't exist" },
];

// Trip length and booking horizon - one value for every portal (unlike lead
// time above), what BookingUtils.checkDuration() reads and enforces on every
// quote()/prebook call. Its own view/update pair and its own Save, so
// changing one card never re-submits the other.
const emptyDurationForm = { minHours: "6", maxHorizonMonths: "3", maxTripMonths: "3" };

const DURATION_FIELDS = [
    { key: "minHours", label: "Minimum trip (hours)", hint: "Shortest booking any portal can create" },
    { key: "maxHorizonMonths", label: "Booking horizon (months)", hint: "How far ahead of today a trip may start" },
    { key: "maxTripMonths", label: "Maximum trip (months)", hint: "Longest booking any portal can create" },
];

const BookingDurationCard = () => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const { form, setForm, loadForm, dirty, discard } = useTrackedForm(emptyDurationForm);

    const load = useCallback(async () => {
        setLoading(true);
        const response = await Handler({ method: "GET", url: "/admin/business/site/booking-duration/view" });
        if (response?.success) {
            const data = response?.data?.data || {};
            loadForm({
                minHours: String(data.minHours ?? 6),
                maxHorizonMonths: String(data.maxHorizonMonths ?? 3),
                maxTripMonths: String(data.maxTripMonths ?? 3),
            });
        }
        setLoading(false);
    }, [loadForm]);

    useEffect(() => { load(); }, [load]);

    const handleChange = (e) => setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

    const onSave = async () => {
        for (const field of DURATION_FIELDS) {
            const value = Number(form[field.key]);
            if (!Number.isInteger(value) || value < 1) {
                toast.error(`${field.label} must be a whole number of 1 or more`);
                return;
            }
        }
        if (Number(form.minHours) > Number(form.maxTripMonths) * 28 * 24) {
            toast.error("Minimum trip can't be longer than the maximum trip");
            return;
        }
        setSaving(true);
        const response = await Handler({
            method: "patch",
            url: "/admin/business/site/booking-duration/update",
            data: {
                minHours: Number(form.minHours),
                maxHorizonMonths: Number(form.maxHorizonMonths),
                maxTripMonths: Number(form.maxTripMonths),
            },
        });
        if (response?.success) {
            toast.success(response?.data?.message || "Booking duration updated");
            load();
        } else {
            toast.error(textCapitalize(response?.data?.message || "Unable to update booking duration"));
        }
        setSaving(false);
    };

    useUnsaved("trip-length", dirty, onSave, discard);

    if (loading) {
        return (
            <div className="settings-card mt-4">
                <Skeleton height={24} width="40%" className="mb-3" />
                <Skeleton height={38} className="mb-3" />
            </div>
        );
    }

    return (
        <div className="settings-card mt-4">
            <h5 className="fw-semibold mb-3">Trip Length</h5>
            <p className="text-muted small mb-4">How short or long a trip can be, and how far ahead it can be booked - the same for every portal, checked on every quote and every booking created.</p>

            <div className="row g-4">
                {DURATION_FIELDS.map((field) => (
                    <div className="col-12 col-md-4" key={field.key}>
                        <label className="form-label" htmlFor={`booking-duration-${field.key}`}>{field.label}</label>
                        <input
                            id={`booking-duration-${field.key}`}
                            type="number"
                            min="1"
                            step="1"
                            name={field.key}
                            className="form-control"
                            value={form[field.key]}
                            onChange={handleChange}
                        />
                        <div className="form-text">{field.hint}</div>
                    </div>
                ))}
            </div>

            <div className="settings-card-actions">
                <button type="button" className="btn btn-primary" onClick={onSave} disabled={saving}>
                    {saving ? "Saving..." : "Save changes"}
                </button>
            </div>
        </div>
    );
};

const BookingTimingTab = () => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const { form, setForm, loadForm, dirty, discard } = useTrackedForm(emptyForm);

    const load = useCallback(async () => {
        setLoading(true);
        const response = await Handler({ method: "GET", url: "/admin/business/site/booking-timing/view" });
        if (response?.success) {
            const data = response?.data?.data || {};
            loadForm({
                admin: String(data.admin ?? 3),
                customer: String(data.customer ?? 3),
                staff: String(data.staff ?? 3),
            });
        }
        setLoading(false);
    }, [loadForm]);

    useEffect(() => { load(); }, [load]);

    const handleChange = (e) => setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

    const onSave = async () => {
        for (const field of FIELDS) {
            const value = Number(form[field.key]);
            if (Number.isNaN(value) || value < 0) {
                toast.error(`${field.label} must be 0 or a positive number`);
                return;
            }
        }
        setSaving(true);
        const response = await Handler({
            method: "patch",
            url: "/admin/business/site/booking-timing/update",
            data: {
                admin: Number(form.admin),
                customer: Number(form.customer),
                staff: Number(form.staff),
            },
        });
        if (response?.success) {
            toast.success(response?.data?.message || "Booking timing updated");
            load();
        } else {
            toast.error(textCapitalize(response?.data?.message || "Unable to update booking timing"));
        }
        setSaving(false);
    };

    useUnsaved("booking-timing", dirty, onSave, discard);

    if (loading) {
        return (
            <div className="settings-card">
                <Skeleton height={24} width="40%" className="mb-3" />
                <Skeleton height={38} className="mb-3" />
                <Skeleton height={38} className="mb-3" />
            </div>
        );
    }

    return (
        <>
        <div className="settings-card">
            <h5 className="fw-semibold mb-3">Booking Timing</h5>
            <p className="text-muted small mb-4">The minimum gap between right now and a booking's start time, checked on every quote and every booking created - set separately for each portal.</p>

            <div className="row g-4">
                {FIELDS.map((field) => (
                    <div className="col-12 col-md-4" key={field.key}>
                        <label className="form-label" htmlFor={`booking-timing-${field.key}`}>{field.label}</label>
                        <input
                            id={`booking-timing-${field.key}`}
                            type="number"
                            min="0"
                            step="0.5"
                            name={field.key}
                            className="form-control"
                            value={form[field.key]}
                            onChange={handleChange}
                        />
                        <div className="form-text">{field.hint}</div>
                    </div>
                ))}
            </div>

            <div className="settings-card-actions">
                <button type="button" className="btn btn-primary" onClick={onSave} disabled={saving}>
                    {saving ? "Saving..." : "Save changes"}
                </button>
            </div>
        </div>
        <BookingDurationCard />
        </>
    );
};

export default BookingTimingTab;
